import { installState, showToast } from '../store.js';
import { installMode, installOfferVisible, promptInstall, snoozeInstallOffer } from '../lib/install.js';
import { __ } from '../i18n.js';
import Icon from './Icon.jsx';

/**
 * "Zet ETM op je startscherm" — the dashboard card that finally makes the
 * install offer visible.
 *
 * Deliberately a card in the existing HH-7 dashboard stack rather than an
 * auto-opening modal: nothing else in this app opens a modal by itself, and
 * a dialog on someone's first login is the pattern everyone learns to
 * dismiss without reading. It sits first because it is only ever on screen
 * while the app is NOT installed — once it is, it never renders again.
 *
 * Two shapes, one card:
 *  - 'prompt' (Chromium/Android): a real button, because we hold a live
 *    beforeinstallprompt event and can open the system dialog.
 *  - 'ios' (Safari): a sentence, because iOS has no install API at all and
 *    the share sheet is genuinely the only way.
 */
export default function InstallCard() {
  installState.value; // subscribe: re-render when availability changes
  const now = Date.now();

  if (!installOfferVisible(now)) return null;
  const mode = installMode();

  async function handleInstall() {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      showToast(__('ETM staat nu op je startscherm.', 'etm'), 'success');
    }
    // 'dismissed'/'unavailable' need no message: the browser's own dialog
    // was the whole interaction, and the card simply stops offering because
    // the event is spent. Chrome re-fires it on a later load.
  }

  return (
    <div class="etm-dashboard-card etm-dashboard-card--install">
      <div class="etm-dashboard-card__header etm-dashboard-card__header--static">
        <span class="etm-dashboard-card__icon">
          <Icon name={mode === 'ios' ? 'ios_share' : 'mobile_arrow_down'} size={20} />
        </span>
        <span class="etm-dashboard-card__title">{__('Zet ETM op je startscherm', 'etm')}</span>
      </div>
      <p class="etm-dashboard-card__empty">
        {mode === 'ios'
          ? __('Tik onderin op Deel en kies "Zet op beginscherm". ETM opent dan direct als app.', 'etm')
          : __('Dan opent ETM direct als app — sneller, en zonder browserbalk.', 'etm')}
      </p>
      <div class="etm-install-actions">
        {mode === 'prompt' ? (
          <button type="button" class="etm-btn etm-btn-primary etm-btn-sm" onClick={handleInstall}>
            <Icon name="mobile_arrow_down" size={20} /> {__('Installeren', 'etm')}
          </button>
        ) : null}
        <button type="button" class="etm-btn etm-btn-ghost etm-btn-sm" onClick={() => snoozeInstallOffer(Date.now())}>
          {__('Later', 'etm')}
        </button>
      </div>
    </div>
  );
}
